import { useState, useRef, useEffect, useMemo } from 'react';
import useCTraderSocket from './hooks/useCTraderSocket';
import StatusBadge from './components/StatusBadge';
import ChartModal from './components/ChartModal';
import { TRACKED_SYMBOLS } from './lib/constants';
import { fmt, timeAgo } from './lib/formatters';

function FearGreedBar({ value, coinCount }) {
  if (value == null) return null;

  const alertClass =
    value < 25 ? 'alert-error' :
    value < 45 ? 'alert-warning' :
    value < 55 ? 'alert-info' :
    value < 75 ? 'alert-success' :
    'alert-warning';

  const label =
    value < 25 ? 'Extreme Fear' :
    value < 45 ? 'Fear' :
    value < 55 ? 'Neutral' :
    value < 75 ? 'Greed' :
    'Extreme Greed';

  return (
    <div className={`alert ${alertClass}`}>
      <i className="fa-solid fa-gauge-high text-xl"></i>
      <div>
        <div className="font-bold">Fear & Greed Index: {value}</div>
        <div className="text-sm">{label}</div>
      </div>
      <div className="text-sm opacity-70">{coinCount} coins tracked</div>
    </div>
  );
}

function LivePrice({ price, decimals }) {
  const ref = useRef(null);
  const prevPrice = useRef(price);

  useEffect(() => {
    if (price !== prevPrice.current && ref.current) {
      ref.current.classList.add('text-warning');
      const timer = setTimeout(() => ref.current?.classList.remove('text-warning'), 300);
      prevPrice.current = price;
      return () => clearTimeout(timer);
    }
  }, [price]);

  return (
    <span ref={ref} className="font-mono text-xl font-bold transition-colors">
      {price ? fmt(price, decimals) : '-'}
    </span>
  );
}

function TaBadges({ rsi, macdSignal, price, ma50 }) {
  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      {rsi != null && (
        <div className="tooltip" data-tip="RSI(14)">
          <span className={`badge badge-sm ${rsi > 70 ? 'badge-error' : rsi < 30 ? 'badge-success' : 'badge-ghost'}`}>
            RSI {rsi.toFixed(1)}
          </span>
        </div>
      )}
      {macdSignal != null && (
        <div className="tooltip" data-tip="MACD Signal">
          <span className={`badge badge-sm ${macdSignal > 0 ? 'badge-success' : 'badge-error'}`}>
            MACD {macdSignal > 0 ? '+' : ''}{macdSignal.toFixed(2)}
          </span>
        </div>
      )}
      {ma50 != null && price != null && (
        <div className="tooltip" data-tip="Price vs SMA50">
          <span className={`badge badge-sm ${price > ma50 ? 'badge-success' : 'badge-error'}`}>
            {price > ma50 ? 'Above' : 'Below'} MA50
          </span>
        </div>
      )}
    </div>
  );
}

function AiAnalysis({ text }) {
  if (!text) return null;

  return (
    <div className="mt-2">
      <div className="text-xs font-semibold opacity-50 mb-1">
        <i className="fa-solid fa-robot mr-1"></i>AI Analysis
      </div>
      <p className="text-xs opacity-70 leading-relaxed">{text}</p>
    </div>
  );
}

function MarketCard({ snapshot, livePrice, onClick }) {
  const symbol = snapshot.asset;
  const name = symbol.replace('USD', '');
  const price = livePrice || snapshot.price;
  const decimals = price > 100 ? 2 : price > 1 ? 4 : 6;

  return (
    <div
      className="card bg-base-200 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
      onClick={onClick}
      title={`Click to view ${name} chart`}
    >
      <div className="card-body p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base">{name}/USD</h2>
          <i className="fa-solid fa-chart-line text-xs opacity-30"></i>
        </div>

        <div className="mt-1">
          <LivePrice price={price} decimals={decimals} />
        </div>

        <div className="flex items-center justify-between text-xs opacity-60">
          {snapshot.volume24h != null && (
            <span>Vol: <span className="font-mono">{fmt(snapshot.volume24h, 0)}</span></span>
          )}
          {snapshot.recordedAt && (
            <span className="tooltip tooltip-left" data-tip={new Date(snapshot.recordedAt).toLocaleString()}>
              {timeAgo(new Date(snapshot.recordedAt))}
            </span>
          )}
        </div>

        <TaBadges
          rsi={snapshot.rsi}
          macdSignal={snapshot.macdSignal}
          price={price}
          ma50={snapshot.ma50}
        />

        <AiAnalysis text={snapshot.aiAnalysis} />
      </div>
    </div>
  );
}

export default function MarketApp({ config, snapshots = [] }) {
  const { status, prices } = useCTraderSocket(config);
  const [chartSymbol, setChartSymbol] = useState(null);
  const [search, setSearch] = useState('');

  const fearGreed = useMemo(() => {
    for (const s of snapshots) {
      if (s.fearGreedIndex != null) return s.fearGreedIndex;
    }
    return null;
  }, [snapshots]);

  const filtered = useMemo(() => {
    if (!search.trim()) return snapshots;
    const q = search.toLowerCase();
    return snapshots.filter(s =>
      s.asset.toLowerCase().includes(q) ||
      s.asset.replace('USD', '').toLowerCase().includes(q)
    );
  }, [snapshots, search]);

  const getLivePrice = (asset) => {
    const symbolId = TRACKED_SYMBOLS[asset];
    if (!symbolId) return null;
    return prices[symbolId]?.bid || null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <StatusBadge status={status} />
        <div className="flex-1">
          <div className="relative">
            <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 opacity-40 text-sm"></i>
            <input
              type="text"
              placeholder="Search symbols..."
              className="input input-bordered input-sm w-full max-w-xs pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <span className="text-xs opacity-50">{filtered.length} symbols</span>
      </div>

      <FearGreedBar value={fearGreed} coinCount={snapshots.length} />

      {status === 'connecting' && snapshots.length === 0 && (
        <div className="flex items-center justify-center py-12 opacity-40">
          <span className="loading loading-spinner loading-lg mr-3"></span>
          Connecting to market data...
        </div>
      )}

      {snapshots.length === 0 && status !== 'connecting' ? (
        <div className="card bg-base-200 shadow-xl">
          <div className="card-body text-center py-12 opacity-40">
            <i className="fa-solid fa-chart-column text-5xl mb-3"></i>
            <p>Waiting for market data</p>
            <p className="text-sm mt-1">Agent collects snapshots hourly for all crypto symbols</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((snapshot) => (
            <MarketCard
              key={snapshot.asset}
              snapshot={snapshot}
              livePrice={getLivePrice(snapshot.asset)}
              onClick={() => setChartSymbol(snapshot.asset)}
            />
          ))}
        </div>
      )}

      <ChartModal symbol={chartSymbol} onClose={() => setChartSymbol(null)} />
    </div>
  );
}
